import type { CSSProperties } from 'react';
/** Lesbarer Text auch auf sehr hellen eigenen Farben (WCAG-Kontrast schwarz/weiß). */
export function accentStyle(color?: string | null): CSSProperties {
  if (!color || !/^#[0-9a-f]{6}$/i.test(color)) return {};
  const rgb = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
  const linear = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const lum = linear[0]! * 0.2126 + linear[1]! * 0.7152 + linear[2]! * 0.0722;
  return { '--color-accent': color, '--color-on-accent': lum > 0.179 ? '#18181b' : '#ffffff', '--color-accent-hover': color } as CSSProperties;
}

/** Relative Leuchtdichte nach WCAG */
export function luminance(color: string): number {
  const rgb = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16) / 255);
  const [r, g, b] = rgb.map((s) => (s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4));
  return r! * 0.2126 + g! * 0.7152 + b! * 0.0722;
}

/** Kontrastverhältnis zweier Farben (1 … 21) */
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
}

const hex = (rgb: number[]) => '#' + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('');

/**
 * Briefing AF27: nächstgelegene lesbare Variante – die Farbe wird schrittweise zu Schwarz (bzw. Weiß) gemischt, bis
 * sie auf `background` mindestens `min` erreicht. Gibt die Farbe selbst zurück, wenn sie schon reicht.
 */
export function readableOn(color: string, background: string, min = 4.5): string {
  if (!/^#[0-9a-f]{6}$/i.test(color)) return color;
  if (contrast(color, background) >= min) return color.toLowerCase();
  const rgb = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
  const target = luminance(background) > 0.5 ? 0 : 255;
  for (let k = 1; k <= 20; k++) {
    const c = hex(rgb.map((v) => v + (target - v) * (k / 20)));
    if (contrast(c, background) >= min) return c;
  }
  return target ? '#ffffff' : '#000000';
}
